import { useEffect, useMemo, useState } from "react";
import { PublicTransportDownloadSource } from "../../../types/LayerAdmin/PublicTransportDownloadSource";
import SortableTable from "../../Table/SortableTable";
import { TableColumn } from "../../../types/Table/TableColumns";
import { getPublicTransportDownloadSourceStatusLabel, PublicTransportDownloadSourceStatus } from "../../../constant/PublicTransportDownloadSourceStatus";
import { Button, Form, FormSelect, Row } from "react-bootstrap";
import { DeleteSourceModal } from "./DeleteSourceModal";
import { PatchSourceModal } from "./PatchSourceModal";
import { usePublicTransportService } from "../../../hooks";
import { ConfirmMultiIgnoreModal } from "./ConfirmMultiIgnoreModal";
import { set } from "lodash";
import { ConfirmMultiRetryModal } from "./ConfirmMultiRetryModal";
import { useAlertContext } from "../../alert/AlertContext";
import { AlertType } from "../../../types/AlertMessage";


export interface DownloadSourcesTableProps {
    sources: PublicTransportDownloadSource[];
    refreshSources: () => void;
    downloadId: number;
}

export const DownloadSourcesTable = ({ sources: externalSources, refreshSources, downloadId }: DownloadSourcesTableProps) => {
    const [contextSource, setContextSource] = useState<PublicTransportDownloadSource | null>(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showPatchModal, setShowPatchModal] = useState(false);
    const [showMultiIgnoreModal, setShowMultiIgnoreModal] = useState({ show: false, allSources: false });
    const [showRetryModal, setShowRetryModal] = useState({ show: false, allSources: false });
    const [failureReasons, setFailureReasons] = useState<string[]>([]);
    const [filteredFailureReason, setFilteredFailureReason] = useState<string | null>(null);
    const [filteredFailureReasonFull, setFilteredFailureReasonFull] = useState<string | null>(null);
    const [processingMultiIgnore, setProcessingMultiIgnore] = useState(false);
    const publicTransportService = usePublicTransportService();
    const [sources, setSources] = useState<PublicTransportDownloadSource[]>(externalSources);
    const { showAlert } = useAlertContext();

    useEffect(() => {
        updateFailureReasons();
    }, [sources]);

    const updateFailureReasons = () => {
        const failedSources = sources.filter(x =>
            x.failureReason != null &&
            x.failureReason != undefined &&
            x.status == PublicTransportDownloadSourceStatus.Failed
        );

        const reasons = failedSources
            .sort((a, b) => (a.failureReason ?? "").localeCompare(b.failureReason ?? ""))
            .map((source: PublicTransportDownloadSource) =>
                "(" + failedSources.filter(x => x.failureReason == source.failureReason).length + ") " + source.failureReason
            );

        const uniqueReasons = Array.from(
            new Set(
                reasons.filter((reason): reason is string =>
                    reason !== undefined && reason !== null && reason !== "" && reason.trim() !== ""
                )
            )
        );

        setFailureReasons(uniqueReasons);
    }

    const renderActions = (source: PublicTransportDownloadSource) => {
        switch (source.status) {
            case PublicTransportDownloadSourceStatus.Failed:
                return renderFailedActions(source);
            case PublicTransportDownloadSourceStatus.Success:
                return renderSuccessActions(source);
            case PublicTransportDownloadSourceStatus.Ignored:
                return renderIgnoredActions(source);
            case PublicTransportDownloadSourceStatus.Patched:
                return renderPatchedActions(source);
        }
    }

    const renderFailedActions = (source: PublicTransportDownloadSource) => {
        return <>
            <Button onClick={() => { setContextSource(source); setShowPatchModal(true); }}>Upload</Button>
            {" "}<Button onClick={() => {
                publicTransportService.IgnoreDownloadSource(source.id).then(() => {
                    refreshPostDeleteUi(source);
                }).catch((error: any) => {

                });
            }}>Ignore</Button>
        </>
    }

    const renderIgnoredActions = (source: PublicTransportDownloadSource) => {
        return <>
            <Button onClick={() => { setContextSource(source); setShowPatchModal(true); }}>Upload</Button>
        </>
    }

    const renderSuccessActions = (source: PublicTransportDownloadSource) => {
        return <>
            <Button onClick={() => { setContextSource(source); setShowPatchModal(true); }}>Overwrite</Button>
            {" "}<Button onClick={() => { setContextSource(source); setShowDeleteModal(true); }}>Delete and Ignore</Button>
        </>
    }

    const renderPatchedActions = (source: PublicTransportDownloadSource) => {
        return <>
            <Button onClick={() => { setContextSource(source); setShowPatchModal(true); }}>Overwrite</Button>
            {" "}<Button onClick={() => { setContextSource(source); setShowDeleteModal(true); }}>Delete and Ignore</Button>
        </>
    }

    const downloadSourceFromS3 = (source: PublicTransportDownloadSource) => {
        publicTransportService.DownloadSource(source.id).then(() => {
        }).catch((error: any) => {
        });
    };

    const columns: TableColumn[] = [
        {
            displayName: "Name",
            field: "name",
            sortable: true,
            renderContents: (value: any, source: PublicTransportDownloadSource) => {
                return (source.status == PublicTransportDownloadSourceStatus.Patched || source.status == PublicTransportDownloadSourceStatus.Success) ?
                    (<a href="#" title="Download source from S3" onClick={() => { downloadSourceFromS3(source); }} >{value}</a>)
                    : value;
            },
            maxWidth: 280
        },
        {
            displayName: "Status",
            field: "status",
            sortable: true,
            renderContents: (value: any) => getPublicTransportDownloadSourceStatusLabel(value)
        },
        {
            displayName: "Failure Reason",
            field: "failureReason",
            sortable: true
        },
        {
            displayName: "Previous Quarter Data",
            field: "existsInPreviousQuarter",
            sortable: false,
            minWidth: 200
        },
        {
            displayName: "Source URL",
            field: "sourceUrl",
            sortable: false,
            renderContents: (value: any, source: PublicTransportDownloadSource) => {
                if (source.sourceUrl) {
                    return <a href={value}>Click Here</a>
                }
            },
            minWidth: 120
        },
        {
            displayName: "Actions",
            field: "",
            sortable: false,
            renderContents: (value: any, source: PublicTransportDownloadSource) => {
                return renderActions(source);
            },
            minWidth: 280
        }
    ];
    const refreshPostDeleteUi = (s: PublicTransportDownloadSource) => {
        if (!s) return;

        const updatedSource = { ...s, status: PublicTransportDownloadSourceStatus.Ignored };
        setSources(prevSources => [
            ...prevSources.filter(source => source.id !== s.id),
            updatedSource
        ]);
        refreshSources();
    }

    const refreshPostPatchUi = (s: PublicTransportDownloadSource) => {
        if (!s) return;

        const updatedSource = { ...s, status: PublicTransportDownloadSourceStatus.Patched };
        setSources(prevSources => [
            ...prevSources.filter(source => source.id !== s.id),
            updatedSource
        ]);
        refreshSources();
    }

    const ignoreFilteredSourcesConfirmed = async () => {
        const listToIgnore = showMultiIgnoreModal.allSources
            ? sources.filter(x => x.status == PublicTransportDownloadSourceStatus.Failed)
            : sources.filter(x => x.failureReason == filteredFailureReason && x.status == PublicTransportDownloadSourceStatus.Failed);

        setProcessingMultiIgnore(true);

        const batchSize = 10;
        try {
            for (let i = 0; i < listToIgnore.length; i += batchSize) {
                const batch = listToIgnore.slice(i, i + batchSize);
                await Promise.all(batch.map((source: PublicTransportDownloadSource) => {
                    return publicTransportService.IgnoreDownloadSource(source.id).then(() => {
                        refreshPostDeleteUi(source);
                    }).catch((error: any) => {
                        console.error(error);
                    });
                }));
            }

            setProcessingMultiIgnore(false);
            setShowMultiIgnoreModal({ show: false, allSources: false });
            if (!showMultiIgnoreModal.allSources) {
                setFilteredFailureReason(null);
                setFilteredFailureReasonFull(null);
            }
        } catch (error: any) {
            console.error(error);
            setProcessingMultiIgnore(false);
            updateFailureReasons();
        }
    }

    return <>

        <Form.Group className="w-100 mb-2">
            <Button onClick={() => { setShowRetryModal({ show: true, allSources: true }) }}>Retry all Failed</Button>
            {" "}<Button onClick={() => { setShowMultiIgnoreModal({ show: true, allSources: true }); }}>Ignore all Failed</Button>
        </Form.Group>
        <Form.Group className="w-100 mb-2">
            <Form.Label className="mb-0">Select a Failure Reason</Form.Label>
            <FormSelect
                onChange={(x) => { setFilteredFailureReason(x.target.value == "" ? null : x.target.value); setFilteredFailureReasonFull(x.target.value == "" ? null : x.target.value); }}
                name="layer"
                value={filteredFailureReasonFull ?? ""}
            >
                <option value="">{`No Filter`}</option>
                {failureReasons.map((option: any, index: number) => (
                    <option key={index} value={option.substring(option.indexOf(" ") + 1)}>
                        {option}
                    </option>
                ))}
            </FormSelect>
        </Form.Group>

        {filteredFailureReason != null && <Form.Group className="w-100 mb-2">
            <Button onClick={() => setShowRetryModal({ show: true, allSources: false })}>Retry All Filtered</Button>&nbsp;
            <Button onClick={() => { setShowMultiIgnoreModal({ show: true, allSources: false }); }}>Ignore all Filtered</Button>
        </Form.Group>} 

        <SortableTable data={sources.filter(x => filteredFailureReason == null ? true : x.failureReason == filteredFailureReason && x.status == PublicTransportDownloadSourceStatus.Failed)} columns={columns} />
        {contextSource && (
            <>
                <DeleteSourceModal show={showDeleteModal} onDelete={() => refreshPostDeleteUi(contextSource)} onClose={() => { setShowDeleteModal(false); }} source={contextSource} />
                <PatchSourceModal show={showPatchModal} onPatched={() => refreshPostPatchUi(contextSource)} onClose={() => { setShowPatchModal(false); }} source={contextSource} />
            </>
        )}
        <ConfirmMultiIgnoreModal
            show={showMultiIgnoreModal.show}
            onConfirmed={ignoreFilteredSourcesConfirmed}
            onClose={() => { setShowMultiIgnoreModal({ show: false, allSources: false }); }}
            sources={showMultiIgnoreModal.allSources
                ? sources.filter(x => x.status == PublicTransportDownloadSourceStatus.Failed)
                : sources.filter(x => x.failureReason == filteredFailureReason && x.status == PublicTransportDownloadSourceStatus.Failed)}
            isIgnoring={processingMultiIgnore} />
        <ConfirmMultiRetryModal
            show={showRetryModal.show}
            onClose={() => { setShowRetryModal({ show: false, allSources: false }); }}
            sources={sources.filter(x => !showRetryModal.allSources ? x.failureReason == filteredFailureReason && x.status == PublicTransportDownloadSourceStatus.Failed : x.status == PublicTransportDownloadSourceStatus.Failed)}
            isIgnoring={processingMultiIgnore}
            failureReason={!showRetryModal.allSources && filteredFailureReason ? filteredFailureReason : undefined}
            onConfirmed={() => {
                setShowRetryModal({ show: false, allSources: false });
                publicTransportService.RetryDownloadSourceDownloads(downloadId, showRetryModal.allSources ? null : filteredFailureReason).then(() => {
                    showAlert({type: AlertType.Information, message: "Retrying download sources. Support will receive an email once this process finishes."});
                }).catch((error: any) => {
                });
            }} />
    </>
};